export type LiveTransportMode = 'legacy_direct' | 'backend_mediated';

export interface LiveTransportInput {
  mode?: LiveTransportMode;
  apiKey?: string;
  accessToken?: string;
  wsUrl?: string;
  provider?: 'vertex_ai' | 'gemini_api' | 'vertex_ai_direct' | 'backend_bridge';
  gcpProjectId?: string;
  gcpRegion?: string;
  model: string;
}

export interface LiveTransportAdapter {
  mode: LiveTransportMode;
  buildWebSocketUrl(): string;
  resolveSetupModel(): string;
}

const DEFAULT_GEMINI_WS_URL =
  'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent';

const appendQueryParam = (baseUrl: string, key: string, value: string): string => {
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

class LegacyDirectTransport implements LiveTransportAdapter {
  mode: LiveTransportMode = 'legacy_direct';
  private readonly input: LiveTransportInput;

  constructor(input: LiveTransportInput) {
    this.input = input;
  }

  buildWebSocketUrl(): string {
    const baseUrl = this.input.wsUrl || DEFAULT_GEMINI_WS_URL;
    if (this.input.accessToken) {
      return appendQueryParam(baseUrl, 'access_token', this.input.accessToken);
    }
    if (this.input.apiKey) {
      return appendQueryParam(baseUrl, 'key', this.input.apiKey);
    }
    return baseUrl;
  }

  resolveSetupModel(): string {
    if (this.input.model.startsWith('projects/')) {
      return this.input.model;
    }
    if ((this.input.provider === 'vertex_ai' || this.input.provider === 'vertex_ai_direct') && this.input.gcpProjectId) {
      const region = this.input.gcpRegion || 'us-central1';
      return `projects/${this.input.gcpProjectId}/locations/${region}/publishers/google/models/${this.input.model}`;
    }
    return `models/${this.input.model}`;
  }
}

class BackendMediatedTransport implements LiveTransportAdapter {
  mode: LiveTransportMode = 'backend_mediated';
  private readonly input: LiveTransportInput;

  constructor(input: LiveTransportInput) {
    this.input = input;
  }

  buildWebSocketUrl(): string {
    if (!this.input.wsUrl) {
      throw new Error('Missing ws_url for backend-mediated transport');
    }
    // Security invariant: browser websocket URL must not include access_token.
    return this.input.wsUrl;
  }

  resolveSetupModel(): string {
    if (this.input.model.startsWith('projects/')) {
      return this.input.model;
    }
    return `models/${this.input.model}`;
  }
}

export const createLiveTransport = (input: LiveTransportInput): LiveTransportAdapter => {
  const envMode = (import.meta.env.VITE_LIVE_TRANSPORT_MODE as LiveTransportMode | undefined) ?? 'legacy_direct';
  const requested = input.mode ?? envMode;
  const effectiveMode: LiveTransportMode =
    input.provider === 'backend_bridge' || requested === 'backend_mediated'
      ? 'backend_mediated'
      : 'legacy_direct';

  if (effectiveMode === 'backend_mediated') {
    return new BackendMediatedTransport(input);
  }
  return new LegacyDirectTransport(input);
};
